// src/pages/ArtistProfilePageBigPicture.js
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArtistProfileCard from '../components/ArtistProfileCard';
import MiniArtworkCard from '../components/MiniArtworkCard';
import { EngagementSummary } from '../components/EngagementSummary';
import CollapsibleComments from '../components/CollapsibleComments';
import { publicGet } from '../lib/apiPublic';

// Normalize any absolute media URL to a relative one so CRA proxy handles it.
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/media/')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url.replace(/^https?:\/\/[^/]+/, '');
  }
  const trimmed = url.replace(/^\.?\/*/, '');
  return trimmed.startsWith('media/') ? `/${trimmed}` : `/media/${trimmed}`;
}

// Helper: collect all image URLs for an artwork
function getImageUrlsForArtwork(art) {
  if (!art) return [];
  const urls = [];
  if (Array.isArray(art.images)) {
    for (const im of art.images) {
      const src = im?.url || im?.image || im?.image_url;
      if (src) urls.push(toRelativeMedia(src));
    }
  }
  if (urls.length === 0 && (art.primary_image_url || art.image || art.image_url)) {
    urls.push(toRelativeMedia(art.primary_image_url || art.image || art.image_url));
  }
  const seen = new Set();
  return urls.filter((u) => (seen.has(u) ? false : (seen.add(u), true)));
}

export default function ArtistProfilePageBigPicture() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);

  // lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeArtworkIndex, setActiveArtworkIndex] = useState(0);
  const [innerImageIndex, setInnerImageIndex] = useState({}); // per-artwork inner slide
  const [infoHidden, setInfoHidden] = useState(false);        // slide panel down/up

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    publicGet(`/artists/${id}/`, { params: { view: "card" } }).then((res) => setArtist(res.data));
    publicGet(`/artworks/?artist=${id}&view=card`).then((res) => {
      setArtworks(res.data || []);
      setActiveArtworkIndex(0);
      setInnerImageIndex({});
      setInfoHidden(false);
    });
  }, [id]);

  const hasArtworks = Array.isArray(artworks) && artworks.length > 0;

  const fullName = artist
    ? (artist.display_name ||
      `${artist.user?.first_name || ''} ${artist.user?.last_name || ''}`.trim() ||
      artist.user?.username)
    : '';

  const heroImage = useMemo(() => {
    if (!hasArtworks) return null;
    const first = artworks[0];
    const list = getImageUrlsForArtwork(first);
    return list.length ? list[0] : null;
  }, [artworks, hasArtworks]);

  const scrollToSection = (id) => () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openLightboxForIndex = (index) => {
    if (!hasArtworks) return;
    setActiveArtworkIndex(index);
    setInfoHidden(false);       // reset panel visible when opening
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const clampArtworkIndex = (idx) => {
    if (!hasArtworks) return 0;
    const len = artworks.length;
    return (idx + len) % len;
  };

  const changeArtwork = (delta) => {
    if (!hasArtworks) return;
    setActiveArtworkIndex((prev) => clampArtworkIndex(prev + delta));
    setInfoHidden(false);       // show panel again when changing artwork
  };

  const changeInnerImage = (artId, imageCount, delta) => {
    if (!imageCount || imageCount <= 1) return;
    setInnerImageIndex((prev) => {
      const current = prev[artId] || 0;
      const next = ((current + delta) % imageCount + imageCount) % imageCount;
      return { ...prev, [artId]: next };
    });
  };

  if (!artist) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'grey.200',
          backgroundColor: 'transparent',
        }}
      >
        <Typography variant="h6">Loading artist profile...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        color: '#f9fafb',
        backgroundColor: 'transparent', // let GlobalBlurredCollage show
        position: 'relative',
      }}
    >
      {/* Fixed top nav (Intro / Profile / Gallery) */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          backdropFilter: 'blur(14px)',
          backgroundColor: 'rgba(6, 12, 32, 0.86)',
          borderBottom: '1px solid rgba(148,163,184,0.35)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, letterSpacing: 1 }}
          >
            ARTflow
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 2, md: 3 },
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            <Typography
              component="button"
              onClick={scrollToSection('intro-section')}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                color: 'rgba(209,213,219,0.95)',
                '&:hover': { color: '#7dd3fc' },
              }}
            >
              Intro
            </Typography>
            <Typography
              component="button"
              onClick={scrollToSection('profile-section')}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                color: 'rgba(209,213,219,0.95)',
                '&:hover': { color: '#7dd3fc' },
              }}
            >
              Profile
            </Typography>
            <Typography
              component="button"
              onClick={scrollToSection('gallery-section')}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                color: 'rgba(209,213,219,0.95)',
                '&:hover': { color: '#7dd3fc' },
              }}
            >
              Gallery
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Intro (full-screen hero) */}
      <Box
        id="intro-section"
        component="section"
        sx={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          pt: { xs: 10, md: 12 }, // offset for fixed nav
          pb: { xs: 6, md: 8 },
        }}
      >
        {/* Hero background image (from first artwork) */}
        {heroImage && (
          <Box
            component="div"
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(6,12,32,0.90)), url(${heroImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(3px)',
              transform: 'scale(1.03)',
              opacity: 0.85,
              zIndex: 0,
            }}
          />
        )}
        {!heroImage && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at top, rgba(56,189,248,0.25), transparent 55%),' +
                'radial-gradient(circle at bottom, rgba(94,234,212,0.18), transparent 55%),' +
                'rgba(15,23,42,0.96)',
              zIndex: 0,
            }}
          />
        )}

        <Container
          maxWidth="md"
          sx={{
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Typography
            variant={isDesktop ? 'h2' : 'h3'}
            sx={{
              fontWeight: 700,
              mb: 2,
              textShadow: '0 18px 40px rgba(0,0,0,0.75)',
            }}
          >
            {fullName}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              maxWidth: 640,
              mx: 'auto',
              mb: 3,
              color: 'rgba(226,232,240,0.96)',
            }}
          >
            Featured ARTflow artist. Scroll through their story and explore a
            full-screen gallery of their work.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={scrollToSection('profile-section')}
              sx={{
                px: 3,
                borderRadius: 999,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              View Profile
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={scrollToSection('gallery-section')}
              sx={{
                px: 3,
                borderRadius: 999,
                textTransform: 'uppercase',
                letterSpacing: 1,
                borderColor: 'rgba(226,232,240,0.7)',
              }}
            >
              Open Gallery
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Profile section */}
      <Box
        id="profile-section"
        component="section"
        sx={{
          py: { xs: 6, md: 8 },
          background: 'transparent',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(148,163,184,0.9)',
              letterSpacing: 2,
            }}
          >
            Profile
          </Typography>
          <Typography
            variant="h4"
            sx={{ mb: 3, fontWeight: 600, color: 'grey.50' }}
          >
            About the Artist
          </Typography>

          <ArtistProfileCard user={artist} />
        </Container>
      </Box>

      {/* Gallery section */}
      <Box
        id="gallery-section"
        component="section"
        sx={{
          py: { xs: 6, md: 8 },
          background: 'transparent',
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(148,163,184,0.9)',
              letterSpacing: 2,
            }}
          >
            Gallery
          </Typography>
          <Typography
            variant="h4"
            sx={{ mb: 3, fontWeight: 600, color: 'grey.50' }}
          >
            Selected Works
          </Typography>

          {hasArtworks ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(220px, 1fr))',
                  md: 'repeat(auto-fill, minmax(260px, 1fr))',
                },
                gap: 2.5,
              }}
            >
              {artworks.map((art, idx) => (
                <MiniArtworkCard
                  key={art.id || idx}
                  artwork={art}
                  width="100%"
                  imgHeight={180}
                  onClick={() => openLightboxForIndex(idx)}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ color: 'grey.300', mt: 2 }}>
              No artworks uploaded yet.
            </Typography>
          )}
        </Container>
      </Box>

      {/* BigPicture-style lightbox overlay */}
      {lightboxOpen && hasArtworks && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 40,
            bgcolor: 'rgba(3,7,18,0.97)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1.5, md: 4 },
          }}
        >
          {(() => {
            const art = artworks[clampArtworkIndex(activeArtworkIndex)];
            const artId = art.id ?? clampArtworkIndex(activeArtworkIndex);
            const imageUrls = getImageUrlsForArtwork(art);
            const innerIdx = innerImageIndex[artId] || 0;
            const activeInnerIdx =
              imageUrls.length > 0 ? innerIdx % imageUrls.length : 0;
            const activeImgUrl =
              imageUrls.length > 0 ? imageUrls[activeInnerIdx] : null;

            return (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 1200,
                  height: { xs: '80vh', md: '85vh' },
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid rgba(148,163,184,0.4)',
                  boxShadow: '0 30px 80px rgba(0,0,0,0.95)',
                  backgroundColor: '#020617',
                }}
              >
                {/* Close button */}
                <IconButton
                  onClick={closeLightbox}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 5,
                    bgcolor: 'rgba(15,23,42,0.9)',
                    color: '#e5e7eb',
                    '&:hover': { bgcolor: 'rgba(15,23,42,1)' },
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* Blurred backdrop */}
                {activeImgUrl && (
                  <Box
                    component="img"
                    src={activeImgUrl}
                    alt=""
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'blur(26px)',
                      transform: 'scale(1.1)',
                      opacity: 0.35,
                    }}
                  />
                )}

                {/* Main image - click toggles info panel */}
                {activeImgUrl && (
                  <Box
                    component="img"
                    src={activeImgUrl}
                    alt={art.title || 'Artwork'}
                    onClick={() => setInfoHidden((prev) => !prev)}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      zIndex: 1,
                      cursor: 'pointer',
                    }}
                  />
                )}

                {/* Outer artwork navigation */}
                {hasArtworks && (
                  <>
                    <IconButton
                    onClick={() => changeArtwork(-1)}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 4,
                        transform: 'translateY(-50%)',
                        bgcolor: 'transparent',
                        color: '#e5e7eb',
                        '&:hover': { bgcolor: 'rgba(15,23,42,0.25)' },
                        zIndex: 4,
                    }}
                    >
                    <ChevronLeftIcon />
                    </IconButton>

                    <IconButton
                    onClick={() => changeArtwork(1)}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 4,
                        transform: 'translateY(-50%)',
                        bgcolor: 'transparent',
                        color: '#e5e7eb',
                        '&:hover': { bgcolor: 'rgba(15,23,42,0.25)' },
                        zIndex: 4,
                    }}
                    >
                    <ChevronRightIcon />
                    </IconButton>
                  </>
                )}

                {/* Inner image slider controls (multi-image artworks) */}
                {imageUrls.length > 1 && (
                <Box
                    sx={{
                    position: 'absolute',
                    top: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1,
                    zIndex: 4,
                    }}
                >
                    <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        changeInnerImage(artId, imageUrls.length, -1);
                    }}
                    sx={{
                        bgcolor: 'rgba(15,23,42,0.85)',
                        color: '#e5e7eb',
                        '&:hover': { bgcolor: 'rgba(15,23,42,1)' },
                    }}
                    >
                    <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        changeInnerImage(artId, imageUrls.length, 1);
                    }}
                    sx={{
                        bgcolor: 'rgba(15,23,42,0.85)',
                        color: '#e5e7eb',
                        '&:hover': { bgcolor: 'rgba(15,23,42,1)' },
                    }}
                    >
                    <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>
                )}

                {/* Info overlay (lower-left corner, BigPicture-style) */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: { xs: '100%', md: '60%' },
                    p: { xs: 2, md: 3 },
                    zIndex: 3,
                    background:
                      'linear-gradient(to top right, rgba(15,23,42,0.95), rgba(15,23,42,0.78), transparent)',
                    borderTopRightRadius: 24,
                    borderTop: '1px solid rgba(148,163,184,0.45)',
                    borderRight: '1px solid rgba(148,163,184,0.45)',
                    transform: infoHidden ? 'translateY(110%)' : 'translateY(0)',
                    opacity: infoHidden ? 0 : 1,
                    pointerEvents: infoHidden ? 'none' : 'auto',
                    transition: 'transform 400ms ease, opacity 400ms ease',
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'rgba(148,163,184,0.9)',
                      letterSpacing: 1,
                    }}
                  >
                    {art.artwork_type || 'Artwork'}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#f9fafb',
                      fontWeight: 600,
                      mb: 0.75,
                    }}
                  >
                    {art.title || 'Untitled'}
                  </Typography>

                  {art.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(209,213,219,0.95)',
                        mb: 1.25,
                      }}
                    >
                      {art.description}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      mb: 1.25,
                    }}
                  >
                    {art.price && (
                      <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                        <strong>Price:</strong> ${art.price}
                      </Typography>
                    )}
                    {art.dimensions && (
                      <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                        <strong>Dimensions:</strong> {art.dimensions}
                      </Typography>
                    )}
                    {art.status && (
                      <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                        <strong>Status:</strong> {art.status}
                      </Typography>
                    )}
                  </Box>

                  {art.id && (
                    <>
                      <Box sx={{ mb: 1 }}>
                        <EngagementSummary
                          target="artwork"
                          objectId={art.id}
                        />
                      </Box>
                      <Box sx={{ mt: 0.5 }}>
                        <CollapsibleComments
                          target="artwork"
                          objectId={art.id}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Box>
      )}
    </Box>
  );
}
